import { notifier } from "../../core/notifier/notifier";
import { BoardConfigAdminApi, type BoardConfigUpdateResponse } from "./board-config-admin-api";

interface BoardConfigAdminFormLike {
    getEditButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
    getRow(root: EventTarget | Element | null | undefined): HTMLFormElement | null;
    getSubmitButton(root: ParentNode): HTMLButtonElement | null;
    getModalUsageDisplay(root: ParentNode): HTMLElement | null;
    getListStyleDisplay(root: ParentNode): HTMLElement | null;
    getTitleEnabledDisplay(root: ParentNode): HTMLElement | null;
    getProductLinkerShowDisplay(root: ParentNode): HTMLElement | null;
    getTargetProductShowDisplay(root: ParentNode): HTMLElement | null;
    getOgpPreviewShowDisplay(root: ParentNode): HTMLElement | null;
    getUserInfoShowDisplay(root: ParentNode): HTMLElement | null;
    getShowContentDisplay(root: ParentNode): HTMLElement | null;
    getRatingOverlayShowDisplay(root: ParentNode): HTMLElement | null;
    getDateShowDisplay(root: ParentNode): HTMLElement | null;
    getUpdatedAtDisplay(root: ParentNode): HTMLElement | null;
    getFieldSelect(root: ParentNode, name: string): HTMLSelectElement | null;
}

export class BoardConfigAdminHandler {
    public form: BoardConfigAdminFormLike;

    public constructor(form: BoardConfigAdminFormLike) {
        this.form = form;
    }

    public handleListClick(event: MouseEvent): void {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const editButton = this.form.getEditButton(target);
        if (!(editButton instanceof HTMLButtonElement)) {
            return;
        }

        event.preventDefault();

        const row = this.form.getRow(editButton);
        if (!(row instanceof HTMLFormElement)) {
            return;
        }

        row.dataset.editing = row.dataset.editing === "1" ? "0" : "1";
    }

    public async submitRow(event: SubmitEvent): Promise<void> {
        event.preventDefault();

        const row = this.form.getRow(event.target);
        if (!(row instanceof HTMLFormElement)) {
            return;
        }

        const submitButton = this.form.getSubmitButton(row);
        const formData = new FormData(row);
        const currentValues = {
            modalUsage: String(row.dataset.currentModalUsage || ""),
            listStyle: String(row.dataset.currentListStyle || ""),
            titleEnabled: String(row.dataset.currentTitleEnabled || ""),
            productLinkerShow: String(row.dataset.currentProductLinkerShow || ""),
            targetProductShow: String(row.dataset.currentTargetProductShow || ""),
            ogpPreviewShow: String(row.dataset.currentOgpPreviewShow || ""),
            userInfoShow: String(row.dataset.currentUserInfoShow || ""),
            showContent: String(row.dataset.currentShowContent || ""),
            ratingOverlayShow: String(row.dataset.currentRatingOverlayShow || ""),
            dateShow: String(row.dataset.currentDateShow || ""),
        };
        const nextValues = {
            modalUsage: String(formData.get("modal_usage") || ""),
            listStyle: String(formData.get("list_style") || ""),
            titleEnabled: String(formData.get("title_enabled") || ""),
            productLinkerShow: String(formData.get("product_linker_show") || ""),
            targetProductShow: String(formData.get("target_product_show") || ""),
            ogpPreviewShow: String(formData.get("ogp_preview_show") || ""),
            userInfoShow: String(formData.get("user_info_show") || ""),
            showContent: String(formData.get("show_content") || ""),
            ratingOverlayShow: String(formData.get("rating_overlay_show") || ""),
            dateShow: String(formData.get("date_show") || ""),
        };

        if (
            currentValues.modalUsage === nextValues.modalUsage &&
            currentValues.listStyle === nextValues.listStyle &&
            currentValues.titleEnabled === nextValues.titleEnabled &&
            currentValues.productLinkerShow === nextValues.productLinkerShow &&
            currentValues.targetProductShow === nextValues.targetProductShow &&
            currentValues.ogpPreviewShow === nextValues.ogpPreviewShow &&
            currentValues.userInfoShow === nextValues.userInfoShow &&
            currentValues.showContent === nextValues.showContent &&
            currentValues.ratingOverlayShow === nextValues.ratingOverlayShow &&
            currentValues.dateShow === nextValues.dateShow
        ) {
            notifier.success("No changes to save.");
            row.dataset.editing = "0";
            return;
        }

        submitButton?.setAttribute("aria-busy", "true");
        if (submitButton) {
            submitButton.disabled = true;
        }

        try {
            const response = await BoardConfigAdminApi.update(row, formData);
            const result = (await response.json().catch(() => ({}))) as BoardConfigUpdateResponse;

            if (!response.ok || result.success !== 1 || !result.item) {
                notifier.error(this.getErrorMessage(result));
                return;
            }

            this.applyRowValues(row, result.item);
            row.dataset.editing = "0";
            notifier.success(
                typeof result.message === "string" && result.message ? result.message : "Board config updated.",
            );
        } catch (error) {
            notifier.error(error instanceof Error && error.message ? error.message : "Board config update failed.");
        } finally {
            submitButton?.removeAttribute("aria-busy");
            if (submitButton) {
                submitButton.disabled = false;
            }
        }
    }

    private getErrorMessage(result: BoardConfigUpdateResponse): string {
        if (typeof result.message === "string" && result.message) {
            return result.message;
        }

        if (result.errors && typeof result.errors === "object") {
            const firstError = Object.values(result.errors).flat()[0];
            if (typeof firstError === "string" && firstError) {
                return firstError;
            }
        }

        return "Board config update failed.";
    }

    private applyRowValues(row: HTMLFormElement, item: NonNullable<BoardConfigUpdateResponse["item"]>): void {
        const modalValue = String(item.modal_usage || "MODELESS");
        const listValue = String(item.list_style || "CARD");
        const titleValue = item.title_enabled !== undefined ? String(item.title_enabled) : "1";
        const linkerValue = String(item.product_linker_show || "SHOW");
        const targetProductValue = String(item.target_product_show || "SHOW");
        const ogpPreviewValue = String(item.ogp_preview_show || "SHOW");
        const userInfoValue = String(item.user_info_show || "SHOW");
        const showContentValue = String(item.show_content || "SHOW");
        const ratingOverlayValue = String(item.rating_overlay_show || "SHOW");
        const dateShowValue = String(item.date_show || "HIDE");

        row.dataset.currentModalUsage = modalValue;
        row.dataset.currentListStyle = listValue;
        row.dataset.currentTitleEnabled = titleValue;
        row.dataset.currentProductLinkerShow = linkerValue;
        row.dataset.currentTargetProductShow = targetProductValue;
        row.dataset.currentOgpPreviewShow = ogpPreviewValue;
        row.dataset.currentUserInfoShow = userInfoValue;
        row.dataset.currentShowContent = showContentValue;
        row.dataset.currentRatingOverlayShow = ratingOverlayValue;
        row.dataset.currentDateShow = dateShowValue;

        this.setSelectValue(row, "modal_usage", modalValue);
        this.setSelectValue(row, "list_style", listValue);
        this.setSelectValue(row, "title_enabled", titleValue);
        this.setSelectValue(row, "product_linker_show", linkerValue);
        this.setSelectValue(row, "target_product_show", targetProductValue);
        this.setSelectValue(row, "ogp_preview_show", ogpPreviewValue);
        this.setSelectValue(row, "user_info_show", userInfoValue);
        this.setSelectValue(row, "show_content", showContentValue);
        this.setSelectValue(row, "rating_overlay_show", ratingOverlayValue);
        this.setSelectValue(row, "date_show", dateShowValue);

        const modalDisplay = this.form.getModalUsageDisplay(row);
        if (modalDisplay) {
            modalDisplay.textContent = `Modal: ${this.getModalUsageLabel(modalValue)}`;
            this.updateBadgeStatus(modalDisplay, modalValue === "MODAL" ? "active" : "pending");
        }

        const listDisplay = this.form.getListStyleDisplay(row);
        if (listDisplay) {
            listDisplay.textContent = `List: ${this.getListStyleLabel(listValue)}`;
            this.updateBadgeStatus(listDisplay, listValue === "CARD" ? "active" : "pending");
        }

        // Content/page settings: themed blue (info) when on — mirrors the
        // board-configs admin blade's row grouping (see index.blade.php).
        const titleDisplay = this.form.getTitleEnabledDisplay(row);
        if (titleDisplay) {
            titleDisplay.textContent = `Title: ${titleValue === "1" ? "Enabled" : "Fixed"}`;
            this.updateBadgeConfig(titleDisplay, titleValue === "1" ? "info" : "muted");
        }

        const showContentDisplay = this.form.getShowContentDisplay(row);
        if (showContentDisplay) {
            showContentDisplay.textContent = `Content: ${showContentValue}`;
            this.updateBadgeConfig(showContentDisplay, showContentValue === "SHOW" ? "info" : "muted");
        }

        const dateShowDisplay = this.form.getDateShowDisplay(row);
        if (dateShowDisplay) {
            dateShowDisplay.textContent = `Date: ${dateShowValue}`;
            this.updateBadgeConfig(dateShowDisplay, dateShowValue === "SHOW" ? "info" : "muted");
        }

        // Product/media preview settings: themed purple (accent) when on.
        const linkerDisplay = this.form.getProductLinkerShowDisplay(row);
        if (linkerDisplay) {
            linkerDisplay.textContent = `Linker: ${linkerValue}`;
            this.updateBadgeConfig(linkerDisplay, linkerValue === "SHOW" ? "accent" : "muted");
        }

        const targetProductDisplay = this.form.getTargetProductShowDisplay(row);
        if (targetProductDisplay) {
            targetProductDisplay.textContent = `Target Product: ${targetProductValue}`;
            this.updateBadgeConfig(targetProductDisplay, targetProductValue === "SHOW" ? "accent" : "muted");
        }

        const ogpPreviewDisplay = this.form.getOgpPreviewShowDisplay(row);
        if (ogpPreviewDisplay) {
            ogpPreviewDisplay.textContent = `OGP: ${ogpPreviewValue}`;
            this.updateBadgeConfig(ogpPreviewDisplay, ogpPreviewValue === "SHOW" ? "accent" : "muted");
        }

        // User/interaction settings: themed orange (warning) when on.
        const userInfoDisplay = this.form.getUserInfoShowDisplay(row);
        if (userInfoDisplay) {
            userInfoDisplay.textContent = `UserInfo: ${userInfoValue}`;
            this.updateBadgeConfig(userInfoDisplay, userInfoValue === "SHOW" ? "warning" : "muted");
        }

        const ratingOverlayDisplay = this.form.getRatingOverlayShowDisplay(row);
        if (ratingOverlayDisplay) {
            ratingOverlayDisplay.textContent = `Overlay: ${ratingOverlayValue}`;
            this.updateBadgeConfig(ratingOverlayDisplay, ratingOverlayValue === "SHOW" ? "warning" : "muted");
        }

        const updatedDisplay = this.form.getUpdatedAtDisplay(row);
        if (updatedDisplay) {
            updatedDisplay.textContent = this.formatLocalDateTime(item.updated_at);
        }
    }

    private setSelectValue(row: HTMLFormElement, name: string, value: string): void {
        const select = this.form.getFieldSelect(row, name);
        if (select) {
            select.value = value;
        }
    }

    private updateBadgeStatus(el: HTMLElement, state: "active" | "inactive" | "pending"): void {
        el.classList.remove("active", "inactive", "pending");
        el.classList.add(state);
    }

    private updateBadgeConfig(el: HTMLElement, state: "success" | "muted" | "info" | "accent" | "warning"): void {
        el.classList.remove(
            "badge-config--success",
            "badge-config--muted",
            "badge-config--info",
            "badge-config--accent",
            "badge-config--warning",
        );
        el.classList.add(`badge-config--${state}`);
    }

    private getModalUsageLabel(value: string): string {
        switch (value) {
            case "MODELESS_TARGET_BLANK":
                return "Modeless (new tab)";
            case "MODAL":
                return "Modal";
            case "DIRECT_MAXIMIZE":
                return "Direct Maximize";
            case "MODELESS":
            default:
                return "Modeless";
        }
    }

    private getListStyleLabel(value: string): string {
        switch (value) {
            case "LIST":
                return "List";
            case "ONE_IMAGE_CARD":
                return "One Image Card";
            case "CARD":
            default:
                return "Card";
        }
    }

    private formatLocalDateTime(value?: string | null): string {
        if (!value) {
            return "-";
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return "-";
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${year}/${month}/${day} ${hours}:${minutes}`;
    }
}
